/**
 * TGSPDCL Tariff Configuration — HT Consumer, Category 1A, 33kV
 * Source: VIREON-CORTEX Variables & Formulas Reference PDF (Telangana)
 * Sample bills: SGR2985 / SGR2502 — January–February 2026
 *
 * All tariff values are sourced from the official variables PDF and
 * cross-verified against the sample TGSPDCL HT bills.
 * DO NOT hardcode any of these values elsewhere in the application.
 * For future tariff revisions, update ONLY this file.
 */

// ─── Time-of-Day Periods ──────────────────────────────────────────────────────

export type ToDPeriod = 'OFF-PEAK' | 'NORMAL' | 'PEAK';

/**
 * TGSPDCL ToD window definitions (dashboard schedule).
 * NORMAL: 10:00–18:00
 * PEAK  : 06:00–10:00 and 18:00–22:00
 * OFF-PEAK: 22:00–06:00
 */
export const TOD_WINDOWS: Record<ToDPeriod, { label: string; range: string }> = {
  'NORMAL':   { label: 'Normal',   range: '10:00–18:00' },
  'PEAK':     { label: 'Peak',     range: '06:00–10:00 · 18:00–22:00' },
  'OFF-PEAK': { label: 'Off-Peak', range: '22:00–06:00' },
};

/** Map an hour-of-day (0–23) to its TGSPDCL ToD period. */
export function todPeriodFor(hour: number): ToDPeriod {
  if ((hour >= 6 && hour < 10) || (hour >= 18 && hour < 22)) return 'PEAK';
  if (hour >= 10 && hour < 18) return 'NORMAL';
  return 'OFF-PEAK';
}

// ─── Energy Charge Rates (Rs / kVAh) — PDF §3 ────────────────────────────────
// Rates are in Rs per kVAh (apparent energy), NOT kWh.
// Three tiers based on Max Demand vs Contract Demand ratio.

/** Tier 1: MD kVA ≤ 1.2 × CD — standard rates */
export const TARIFF_RATES_T1: Record<ToDPeriod, number> = {
  'NORMAL':   7.15,   // 715 paise/kVAh
  'PEAK':     8.65,   // 865 paise/kVAh (normal + 150p TOD premium)
  'OFF-PEAK': 6.65,   // 665 paise/kVAh (normal − 50p off-peak discount)
};

/** Tier 2: 1.2×CD < MD kVA ≤ 2×CD — 1.15× multiplier */
export const TARIFF_RATES_T2: Record<ToDPeriod, number> = {
  'NORMAL':   8.22,   // 1.15 × 7.15
  'PEAK':     9.95,   // 1.15 × 8.65
  'OFF-PEAK': 7.65,   // 1.15 × 6.65
};

/** Tier 3: MD kVA > 2×CD — 1.20× multiplier */
export const TARIFF_RATES_T3: Record<ToDPeriod, number> = {
  'NORMAL':   8.58,   // 1.20 × 7.15
  'PEAK':     10.38,  // 1.20 × 8.65
  'OFF-PEAK': 7.98,   // 1.20 × 6.65
};

/** Default (T1) rates — used before MD is known. */
export const TARIFF_RATES = TARIFF_RATES_T1;

/**
 * Return the applicable rate set for a given MD / CD ratio.
 * PDF §3.1–3.3.
 */
/**
 * Return the applicable rate set for a given MD / CD ratio (PDF §3.1–3.3):
 *   • MD ≤ 1.2×CD          → Tier 1 (standard)
 *   • 1.2×CD < MD ≤ 2×CD   → Tier 2 (×1.15)
 *   • MD > 2×CD            → Tier 3 (×1.20)
 *
 * Used by derivedEngine for per-row energy-charge fields. The billing engines
 * resolve tier rates + labels via the per-state tariff (src/tariffs/<state>.ts).
 */
export function getTariffRates(mdKva: number, cdKva: number): Record<ToDPeriod, number> {
  const ratio = cdKva > 0 ? mdKva / cdKva : 0;
  if (ratio > 2.0) return TARIFF_RATES_T3;
  if (ratio > 1.2) return TARIFF_RATES_T2;
  return TARIFF_RATES_T1;
}

// ─── Demand Charges — PDF §4 / Sample bills ──────────────────────────────────

/** Normal demand charge (Rs / kVA / month). */
export const DEMAND_CHARGE_NORMAL_RS_KVA = 500;

/** Penal demand charge for excess above CD (Rs / kVA / month). */
export const DEMAND_CHARGE_PENAL_RS_KVA  = 1000;

/**
 * Minimum chargeable demand = 80% of Contract Demand.
 * Confirmed from sample bills: CD×0.80 matches "Normal kVA" billed when
 * measured MD is below this floor.
 */
export const MIN_DEMAND_PCT = 0.80;

/**
 * Default Contract Demand (kVA) — override per-meter via SheetSource.meta.contractDemandKva.
 * PDF §1 "CD — Contract Demand".
 */
export const CONTRACT_DEMAND_KVA = 2501;

/** Convenience: MIN_CHARGEABLE_KVA for 2501 kVA CD. */
export const MIN_CHARGEABLE_KVA = CONTRACT_DEMAND_KVA * MIN_DEMAND_PCT;

// ─── Fixed & Statutory Charges — PDF §4 ──────────────────────────────────────

/** Customer charges per month (Rs). PDF §4-E. */
export const CUSTOMER_CHARGES_RS_MONTH = 3500;

/**
 * Electricity Duty (ED) per kVAh consumed (Rs).
 * Formula: E_VAh_current / 1000 × 0.06  →  Rs per kVAh = 6 paise. PDF §4-B.
 */
export const ELECTRICITY_DUTY_RS_KVAH = 0.06;

/** Colony charges per kVAh (Rs). PDF §4-C. Applies only if colony supply is used. */
export const COLONY_CHARGES_RS_KVAH = 7.30;

/** L&F (Line & Feeder?) charges per kVAh (Rs). PDF §4-D. If applicable. */
export const LF_CHARGES_RS_KVAH = 7.15;

/**
 * Late Payment Surcharge (LPSC): 1.25% of bill amount if not paid within
 * 15 days of bill generation date. PDF §4-I.
 */
export const LATE_PAYMENT_SURCHARGE_PCT = 1.25;

// ─── Carbon Emission Factor ───────────────────────────────────────────────────

/** CEA 2023 national grid average emission factor (kg CO₂ / kWh). PDF §2.3 AV (carbon field). */
export const CARBON_KG_PER_KWH = 0.82;

// ─── Sensor Quality Thresholds ────────────────────────────────────────────────

/** Voltage below this threshold = sensor dropout; row excluded from all stats. */
export const VOLTAGE_DROPOUT_V = 100;

/** Percentile used for spike/outlier clamping. */
export const OUTLIER_PERCENTILE = 99.5;

/** Max credible inter-sample gap for trapezoidal energy integration (hours). */
export const MAX_INTEGRATION_GAP_H = 1;

// ─── Power Factor Alert Thresholds ───────────────────────────────────────────

/** PF below this = RKVAH surcharge risk (TGSPDCL penalises low PF). */
export const PF_PENALTY_THRESHOLD  = 0.85;
/** PF at or above this = good range. */
export const PF_TARGET             = 0.95;
/** Sustained floor: below for ≥2 consecutive readings → alert event. */
export const PF_SUSTAINED_FLOOR    = 0.90;

// ─── UI Colours ───────────────────────────────────────────────────────────────

export const TOD_COLORS: Record<ToDPeriod, string> = {
  'NORMAL':   '#0FB18A',   // teal/green
  'PEAK':     '#F97316',   // orange
  'OFF-PEAK': '#118AB2',   // blue
};

// ─── Legacy compatibility shims (still imported by the analytics layer) ──────
// TGSPDCL HT has no PF rebate/surcharge table (it uses an RKVAH surcharge
// handled at the meter level), so these always evaluate to zero. They remain
// because the shared analytics in lib/aggregations.ts and the PF charts import
// them directly. State-specific PF logic lives in src/tariffs/<state>.ts.

export interface PfAdjustmentRow {
  minPf: number; maxPf: number; normalPct: number; peakPct: number; offPeakPct: number;
}
export const PF_ADJUSTMENT_TABLE: PfAdjustmentRow[] = [
  { minPf: 0.95, maxPf: Infinity, normalPct: 0, peakPct: 0, offPeakPct: 0 },
  { minPf: 0.00, maxPf: 0.95,     normalPct: 0, peakPct: 0, offPeakPct: 0 },
];
export function pfAdjustmentPct(_pfWtd: number, _period: ToDPeriod): number { return 0; }

/** PF threshold above which a rebate band begins (display only). */
export const PF_REBATE_THRESHOLD = PF_TARGET;
